/**
 * StarterMemberCard — clickable AI Member card for Welcome onboarding grid.
 *
 * Displays avatar + name + position chip + personality quote + CTA pill.
 * Entire card is interactive; click handler opens the MemberDetailModal.
 */

import { Card } from '@/components/Card/Card';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import type { Member } from '@/features/members/schemas';
import styles from './StarterMemberCard.module.css';

interface Props {
  member: Member;
  onClick: () => void;
}

export function StarterMemberCard({ member, onClick }: Props) {
  const positionName = member.positions?.[0]?.name ?? '';
  const quote = member.personality ?? '';

  const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');

  return (
    <Card padding="md" className={styles.card} onClick={onClick} role="button" tabIndex={0}>
      <div className={styles.avatarWrap}>
        <Avatar name={fullName} kind="ai" size="md" />
      </div>
      <h3 className={styles.name}>{fullName}</h3>
      {positionName && (
        <Chip variant="neutral" size="sm">
          {positionName}
        </Chip>
      )}
      {quote && <p className={styles.quote}>&ldquo;{quote}&rdquo;</p>}
      <span className={styles.pill}>Start with {member.first_name} →</span>
    </Card>
  );
}
